/**
 * LanguageSwitcher Component
 * Toggles between English, French, and Arabic with a styled pill button group.
 * Designed for a light header background.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ButtonGroup,
  Tooltip,
} from '@mui/material';

interface LanguageOption {
  code: string;
  label: string;
  flag: string;
}

const languages: LanguageOption[] = [
  { code: 'en', label: 'English', flag: 'EN' },
  { code: 'ar', label: '\u0627\u0644\u0639\u0631\u0628\u064a\u0629', flag: '\u0639\u0631' },
  { code: 'fr', label: 'Fran\u00e7ais', flag: 'FR' },
];

const LanguageSwitcher: React.FC = () => {
  const { i18n } = useTranslation();

  const resolveCurrentLang = (): string => {
    const lang = i18n.language || 'en';
    if (lang.startsWith('ar')) return 'ar';
    if (lang.startsWith('fr')) return 'fr';
    return 'en';
  };

  const currentLang = resolveCurrentLang();

  const handleLanguageChange = (langCode: string) => {
    i18n.changeLanguage(langCode);
  };

  return (
    <Tooltip title="Language / Langue / \u0627\u0644\u0644\u063a\u0629">
      <ButtonGroup
        size="small"
        variant="outlined"
        aria-label="Language selector"
        sx={{
          '& .MuiButton-root': {
            minWidth: 32,
            px: 0.75,
            py: 0.25,
            fontSize: '11px',
            fontWeight: 600,
            fontFamily: 'var(--font-body)',
            letterSpacing: '0.02em',
            borderColor: 'var(--color-border)',
            color: 'var(--color-text-muted)',
            height: 28,
            '&:hover': {
              borderColor: 'var(--color-accent)',
              bgcolor: 'var(--color-accent-bg)',
              color: 'var(--color-text-primary)',
            },
            transition: 'all var(--transition-fast)',
          },
        }}
      >
        {languages.map((lang) => (
          <Button
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            aria-label={lang.label}
            aria-pressed={currentLang === lang.code}
            sx={{
              bgcolor: currentLang === lang.code
                ? 'var(--color-accent-bg)'
                : 'transparent',
              borderColor: currentLang === lang.code
                ? 'var(--color-accent) !important'
                : 'var(--color-border)',
              color: currentLang === lang.code
                ? 'var(--color-accent-hover)'
                : 'var(--color-text-muted)',
              fontWeight: currentLang === lang.code ? 700 : 600,
              '&:hover': {
                bgcolor: currentLang === lang.code
                  ? 'var(--color-accent-bg)'
                  : 'var(--color-bg-secondary)',
              },
            }}
          >
            {lang.flag}
          </Button>
        ))}
      </ButtonGroup>
    </Tooltip>
  );
};

export default LanguageSwitcher;
